import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import type { JSX } from 'react';

/**
 * Designed empty state for data surfaces (PD-14699): muted icon, a
 * one-line message, optional supporting hint, optional action. Pages
 * decide which variant to show — truly-empty ("No invoices yet") vs
 * filtered-empty ("No invoices in this date range" + a clear/widen
 * action) — this component only owns the consistent rendering.
 *
 * Copy passes through t() here, so callers hand in plain strings.
 */
export function EmptyState({
	icon,
	title,
	hint,
	actionLabel,
	onAction,
}: {
	icon: IconDefinition;
	title: string;
	hint?: string;
	actionLabel?: string;
	onAction?: () => void;
}): JSX.Element {
	const { t } = useTranslation();
	return (
		<div className="text-center py-12 px-4">
			{/* text-brand-muted, not a neutral gray token: the neutral grays
			    (text-disabled, text-faint) measure under 1.5:1 against dark
			    brand-tinted page backgrounds — the brand-aware muted token is
			    the one that stays legible in both modes (PD-15245). */}
			<FontAwesomeIcon icon={icon} className="text-4xl text-brand-muted mb-4" />
			<p className="text-sm font-medium text-default">{t(title)}</p>
			{hint != null && (
				<p className="text-xs text-brand-muted mt-1 max-w-xs mx-auto">
					{t(hint)}
				</p>
			)}
			{actionLabel != null && onAction != null && (
				<button
					type="button"
					onClick={onAction}
					className="mt-4 px-4 py-2 text-sm font-medium rounded-lg border border-brand-border bg-brand-surface hover:border-portal-accent transition-colors min-h-touch"
				>
					{t(actionLabel)}
				</button>
			)}
		</div>
	);
}
